import * as React from "react"
import { Popover as PopoverPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"
import { useTabActive } from "@/hooks/useTabVisit"
import {
  FLOATER_ARROW_HEIGHT,
  FLOATER_ARROW_PADDING,
  FLOATER_ARROW_WIDTH,
  FloaterArrowShape,
  floaterEdgeClass,
} from "./floater-arrow"

function Popover({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  return <PopoverPrimitive.Root data-slot="popover" {...props} />
}

function PopoverTrigger({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
  className,
  align = "center",
  sideOffset = 4,
  arrow = false,
  arrowPadding = FLOATER_ARROW_PADDING,
  container,
  children,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
  /** Points the box at its trigger/anchor. For tips; menus and pickers go without. */
  arrow?: boolean
  /**
   * Where to portal the content. Inside a dialog, pass the dialog's content: the
   * dialog's scroll lock blocks touch scrolling anywhere outside it.
   */
  container?: HTMLElement | null
}) {
  // Drops the floater while its kept-alive tab is hidden (see useTabActive).
  const tabActive = useTabActive()
  if (!tabActive) return null
  return (
    <PopoverPrimitive.Portal container={container}>
      <PopoverPrimitive.Content
        data-slot="popover-content"
        align={align}
        sideOffset={sideOffset}
        arrowPadding={arrowPadding}
        className={cn(
          "z-[80] flex w-72 origin-(--radix-popover-content-transform-origin) flex-col gap-2.5 rounded-lg bg-popover p-2.5 text-sm text-popover-foreground shadow-md outline-hidden floater-motion",
          floaterEdgeClass,
          className
        )}
        {...props}
      >
        {children}
        {arrow && <PopoverArrow />}
      </PopoverPrimitive.Content>
    </PopoverPrimitive.Portal>
  )
}

function PopoverAnchor({
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

function PopoverArrow() {
  return (
    <PopoverPrimitive.Arrow
      data-slot="popover-arrow"
      asChild
      width={FLOATER_ARROW_WIDTH}
      height={FLOATER_ARROW_HEIGHT}
    >
      <FloaterArrowShape />
    </PopoverPrimitive.Arrow>
  )
}

function PopoverHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="popover-header"
      className={cn("flex flex-col gap-0.5 text-sm", className)}
      {...props}
    />
  )
}

function PopoverTitle({ className, ...props }: React.ComponentProps<"h2">) {
  return (
    <div
      data-slot="popover-title"
      className={cn("font-medium", className)}
      {...props}
    />
  )
}

function PopoverDescription({
  className,
  ...props
}: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="popover-description"
      className={cn("text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
}
